import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import { COMPANY_TIMEZONE_OPTIONS, resolveCompanyTimezone } from "../../utils/companyTimezone";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`
  },
  description: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  }
}));

const CompanyTimezoneSettings = ({ company, onUpdated }) => {
  const classes = useStyles();
  const { update } = useCompanies();
  const [loading, setLoading] = useState(false);
  const [timezone, setTimezone] = useState(resolveCompanyTimezone(company?.timezone));

  useEffect(() => {
    setTimezone(resolveCompanyTimezone(company?.timezone));
  }, [company]);

  const canSave = useMemo(() => (
    !!company?.id && !!company?.name && !!company?.email && !!company?.userEmail && !!company?.planId
  ), [company]);

  const handleSave = async () => {
    if (!canSave) {
      toast.error(i18n.t("settings.components.companyTimezone.toasts.companyDataError"));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...company,
        timezone
      };

      const response = await update(payload);
      localStorage.setItem("companyTimezone", timezone);
      toast.success(i18n.t("settings.components.companyTimezone.toasts.updateSuccess"));
      if (typeof onUpdated === "function") {
        onUpdated({
          ...company,
          ...response,
          timezone
        });
      }
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <Typography variant="h6">{i18n.t("settings.components.companyTimezone.title")}</Typography>
      <Typography variant="body2" className={classes.description}>
        {i18n.t("settings.components.companyTimezone.description")}
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="company-timezone-label">{i18n.t("settings.components.companyTimezone.timezone")}</InputLabel>
            <Select
              labelId="company-timezone-label"
              value={timezone}
              onChange={event => setTimezone(event.target.value)}
              label={i18n.t("settings.components.companyTimezone.timezone")}
            >
              {COMPANY_TIMEZONE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            disabled={loading || !canSave}
            onClick={handleSave}
          >
            {loading ? i18n.t("common.buttons.saving") : i18n.t("settings.components.companyTimezone.saveTimezone")}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CompanyTimezoneSettings;
